import {memo, useState} from 'react'
import {Conversation} from '@/features/conversations/types'
import {formatLocaleDate} from '@/dayjs'
import {AlertCircleIcon, Trash01Icon, User01Icon} from '@/components/ui/icon/Icon'
import {useTranslation} from 'react-i18next'
import {AvatarGroup} from '@/components/commons/avatar-group/AvatarGroup'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {capitalize} from '@/utilities/helpers'
import {Button} from '@/components/ui/button/Button'
import {DeleteConversationModal} from '../delete-conversation-modal/DeleteConversationModal'
import {getConversationMemberDisplayName} from '@/features/conversations/utils/participantsUtils'
import {
    StyledConversationBadgeDot,
    StyledConversationContent,
    StyledConversationFooter,
    StyledConversationItem,
    StyledConversationMainInfo
} from './style'
import {useAuthStore, usePermissions} from '@/features/auth/store/store'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {useTheme} from 'styled-components'

interface ConversationItemProps {
    conversation: Conversation
    isSelected?: boolean
    onSelect: (conversation: Conversation) => void
    onDelete?: (conversationId: string) => void
}

const ConversationItemComponent = ({conversation, isSelected, onSelect, onDelete}: ConversationItemProps) => {
    const {t} = useTranslation()
    const currentUser = useAuthStore(state => state.user?.id)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const permissions = usePermissions()
    const {palette} = useTheme()
    const getMembersDisplayText = (): string => {
        if (conversation.members.length === 1) {
            return getConversationMemberDisplayName(conversation.members[0]) || ''
        }

        if (conversation.members.length === 2) {
            const member1 = getConversationMemberDisplayName(conversation.members[0]) || ''
            const member2 = getConversationMemberDisplayName(conversation.members[1]) || ''
            return `${member1} e ${member2}`
        }

        return t('conversations:you_and_others', {count: conversation.members.length - 1})
    }

    const handleDelete = (event: React.MouseEvent) => {
        event.stopPropagation() // Prevent triggering onSelect
        setIsDeleteModalOpen(true)
    }

    const handleDeleteSuccess = () => {
        onDelete?.(conversation.id)
    }

    const handleItemClick = () => {
        if (permissions?.conversations?.write) {
            onSelect(conversation)
        }
    }

    return (
        <>
            <StyledConversationItem
                isSelected={isSelected}
                $isClickable={!!permissions?.conversations?.write}
                onClick={handleItemClick}
                direction="column"
                justify="flex-end"
                align="flex-end"
                fullWidth
            >
                <StyledConversationContent direction="column" gap={2.5} fullWidth>
                    <Flexbox justify="space-between" gap={2} fullWidth>
                        <StyledConversationMainInfo align="center" gap={2}>
                            <Flexbox direction="column">
                                <Flexbox gap={2} align="center">
                                    {conversation.hasUnreadMessages && <StyledConversationBadgeDot />}
                                    <Text size="textSm" palette="neutral" shade="950" weight={500}>
                                        {conversation.title}
                                    </Text>
                                </Flexbox>
                            </Flexbox>
                        </StyledConversationMainInfo>
                        <Flexbox direction="row" align="center" gap={1}>
                            <Text size="textXs" palette="neutral" shade="600" weight={500}>
                                {capitalize(formatLocaleDate(conversation.createdAt, 'ddd DD MMM YYYY'))}
                            </Text>
                            {permissions?.conversations?.write && (
                                <Button variant="ghost" size="xs" shape="square" onClick={handleDelete}>
                                    <Trash01Icon size={14} />
                                </Button>
                            )}
                        </Flexbox>
                    </Flexbox>
                </StyledConversationContent>

                <Flexbox direction="column" gap={1} align="flex-start" fullWidth style={{paddingBottom: 12}}>
                    <Flexbox align="center" gap={1}>
                        <User01Icon size={16} />
                        <Text size="textXs" palette="neutral" shade="600">
                            {currentUser === conversation.firstMessage.sender.id
                                ? t('commons:you')
                                : `${conversation.firstMessage.sender.lastName} ${conversation.firstMessage.sender.firstName}`}
                        </Text>
                        {conversation.totalMessages - 1 > 0 && (
                            <>
                                <Text palette="neutral" shade="500">
                                    •
                                </Text>
                                <Text size="textXs" palette="neutral" shade="600">
                                    {t('conversations:answers_n', {count: conversation.totalMessages - 1})}
                                </Text>
                            </>
                        )}
                    </Flexbox>
                    <Text size="textSm" palette="neutral" shade="950">
                        {conversation.firstMessage.message.slice(0, 150)}
                        {conversation.firstMessage.message.length > 150 && '...'}
                    </Text>
                </Flexbox>

                <StyledConversationFooter align="center" fullWidth>
                    <Flexbox direction="column" align="center" fullWidth gap={3}>
                        <Flexbox fullWidth align="center" gap={1}>
                            <Tooltip
                                variant="light"
                                content={
                                    <Flexbox direction="column" gap={1}>
                                        {conversation.members.map(member => (
                                            <Text size="textXs" palette="neutral" shade="700">
                                                {member.fullName}
                                            </Text>
                                        ))}
                                    </Flexbox>
                                }
                            >
                                <AvatarGroup>
                                    {conversation.members.slice(0, 3).map(member => (
                                        <AvatarGroup.Item
                                            key={member.id}
                                            name={getConversationMemberDisplayName(member) || 'No Name'}
                                            size="xs"
                                        />
                                    ))}
                                </AvatarGroup>
                            </Tooltip>
                            <Text size="textXs" palette="neutral" shade="600" style={{flex: 1}}>
                                {getMembersDisplayText()}
                            </Text>
                        </Flexbox>
                        {conversation.isUrgent && (
                            <Flexbox fullWidth align="center" justify="space-between">
                                <Flexbox gap={2} align="center" justify="flex-end" style={{flexGrow: 1}}>
                                    {conversation.isUrgent && (
                                        <AlertCircleIcon size={16} color={palette.warning[600]} />
                                    )}
                                </Flexbox>
                            </Flexbox>
                        )}
                    </Flexbox>
                </StyledConversationFooter>
            </StyledConversationItem>

            {isDeleteModalOpen && (
                <DeleteConversationModal
                    conversation={conversation}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onDeleteSuccess={handleDeleteSuccess}
                />
            )}
        </>
    )
}

export const ConversationItem = memo(ConversationItemComponent)
